import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { useLocalizedArticles, useLocalizedKnowledgeCategories } from "@/i18n/hooks";

export default function Knowledge() {
    const { t } = useLang();
    const articles = useLocalizedArticles();
    const categories = useLocalizedKnowledgeCategories();
    const [active, setActive] = useState(categories[0]);

    const filtered = active === categories[0] ? articles : articles.filter((a) => a.category === active);
    const featured = articles.find((a) => a.featured);

    return (
        <div data-testid="knowledge-page" className="bg-paper">
            <section className="pt-40 md:pt-48 pb-16 container-editorial">
                <SectionLabel label={t("knowledge.label")} number="B" className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight md:col-span-8 whitespace-pre-line">
                        {t("knowledge.heading")}
                    </h1>
                    <p className="md:col-span-4 text-ink/70 text-lg leading-relaxed">
                        {t("knowledge.intro")}
                    </p>
                </div>
            </section>

            {featured && (
                <section className="pb-24 md:pb-32 container-editorial">
                    <Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center border-t border-border pt-16">
                            <div className="md:col-span-7">
                                <div className="relative aspect-[16/10] overflow-hidden rounded-[12px] shadow-editorial group">
                                    <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1600ms] ease-editorial" />
                                </div>
                            </div>
                            <div className="md:col-span-5">
                                <div className="overlabel text-earth mb-4">{t("common.labels.featured")} · {featured.category}</div>
                                <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mb-6">{featured.title}</h2>
                                <p className="text-ink/70 leading-relaxed text-lg mb-8">{featured.excerpt}</p>
                                <div className="text-xs uppercase tracking-widest text-mist">{featured.readTime}</div>
                            </div>
                        </div>
                    </Reveal>
                </section>
            )}

            <section className="pb-12 container-editorial">
                <div className="flex flex-wrap gap-3">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActive(c)}
                            data-testid={`knowledge-filter-${String(c).toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-5 py-2 rounded-full text-sm border transition-colors duration-300 ${
                                active === c
                                    ? "bg-forest text-paper border-forest"
                                    : "border-border text-ink/70 hover:border-ink hover:text-ink"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="pb-32 container-editorial">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
                    {filtered.map((a, i) => (
                        <Reveal key={a.slug} delay={i * 70}>
                            <article className="group relative overflow-hidden bg-white rounded-[12px] shadow-soft hover:shadow-lift transition-shadow duration-500 h-full flex flex-col" data-testid={`article-card-${a.slug}`}>
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-editorial" />
                                    <div className="absolute top-4 left-4 bg-paper/95 text-ink px-3 py-1 rounded-full text-[10px] uppercase tracking-widest">
                                        {a.category}
                                    </div>
                                </div>
                                <div className="p-8 flex flex-col flex-1">
                                    <div className="flex items-baseline gap-4 mb-3">
                                        <span className="font-serif text-gold text-sm kbd-num">{a.chapter}</span>
                                        <span className="text-xs text-mist uppercase tracking-widest">{a.readTime}</span>
                                    </div>
                                    <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-4">{a.title}</h3>
                                    <p className="text-ink/70 leading-relaxed mb-6 flex-1">{a.excerpt}</p>
                                    <span className="inline-flex items-center gap-2 text-forest text-sm mt-auto">
                                        {t("common.buttons.readEssay")}
                                        <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    </span>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
